import React, { useEffect, useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useParams, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from './components/NavBar';
import Home from './page/RebrandHome';
import PrivateRoute from './components/redirect/PrivateRoute';
import AdminPrivateRoute from './components/redirect/AdminPrivateRoute';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ConversionProvider } from './context/ConversionContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Footer from './components/Footer';
import Termini from './components/Termini';
import Privacy from './components/Privacy';
import Cookies from './components/Cookies';
import Faq from './components/Faq';
import { NotFound, Forbidden, ServerError } from './page/error';
import Analytics from './components/utils/Analytics';
import ScrollToHash from './components/utils/ScrollToHash';
import SeoLinks from './components/utils/SeoLinks';
import './App.css';
import './page/Workspace.css';
import './page/Subpages.css';

const AdminLogin = lazy(() => import('./page/private/AdminLogin'));
const Dashboard = lazy(() => import('./page/Dashboard'));
const Auth = lazy(() => import('./page/Login'));
const UserMessages = lazy(() => import('./page/UserMessages'));
const ConversionList = lazy(() => import('./page/ConversionList'));
const CampaignList = lazy(() => import('./page/CampaingList'));
const UserAnnouncements = lazy(() => import('./page/Announcements'));
const Profile = lazy(() => import('./page/Profile'));
const UserPayments = lazy(() => import('./page/UserPayments'));
const Program = lazy(() => import('./page/AffiliationProgram'));
const Strategy = lazy(() => import('./page/AffiliateStrategies'));
const BonusComparator = lazy(() => import('./page/BonusComparator'));
const Contact = lazy(() => import('./page/Contact'));
const Unsubscribe = lazy(() => import('./page/Unsubscribe'));
const CampaignDetails = lazy(() => import('./page/CampaignDetails'));
const OurPartners = lazy(() => import('./page/OurPartners'));
const Admin = lazy(() => import('./page/private/Admin'));
const AdminMessages = lazy(() => import('./page/private/AdminMessages'));
const ManageCampaign = lazy(() => import('./page/private/ManageCampaign'));
const AllConversion = lazy(() => import('./page/private/AllConversion'));
const AffiliateAnalyticsDashboard = lazy(() => import('./page/private/AffiliateAnalyticsDashboard'));
const AdminAnnouncements = lazy(() => import('./page/private/AdminAnnouncement'));
const UserProfile = lazy(() => import('./page/private/UserProfile'));
const AdminPayments = lazy(() => import('./page/private/AdminPayments'));
const NewsLetter = lazy(() => import('./page/private/NewsLetter'));
const ContactSchema = lazy(() => import('./page/private/AdminContact'));
const BusinessCommunications = lazy(() => import('./page/private/BusinessCommunications'));

// Language wrapper component to handle language detection from URL
function LanguageWrapper() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  
  useEffect(() => {
    // List of supported languages
    const supportedLangs = ['en', 'it'];
    
    // If URL has a valid language code, set it
    if (lang && supportedLangs.includes(lang)) {
      i18n.changeLanguage(lang);
    } else {
      // If no valid language in URL, detect from browser or default to 'en'
      const detectedLang = i18n.language && supportedLangs.includes(i18n.language.substring(0, 2))
        ? i18n.language.substring(0, 2) 
        : 'en';
      
      // If we're at root without language, redirect to language path
      if (lang === undefined && location.pathname === '/') {
        // This will cause a redirect to /{detectedLang}
        i18n.changeLanguage(detectedLang);
      }
    }
  }, [lang, i18n, location]);
  
  // If we're at the root path and no language is specified, redirect to detected language
  if (lang === undefined && location.pathname === '/') {
    const detectedLang = i18n.language?.substring(0, 2) === 'it' ? 'it' : 'en';
    return <Navigate to={`/${detectedLang}`} replace />;
  }
  
  if (lang && !['it', 'en'].includes(lang)) return <NotFound />;
  return <Outlet />;
}

function Layout({ children }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const path = location.pathname.replace(/^\/(it|en)(?=\/|$)/, '') || '/';
  const internal = /^\/(dashboard|conversionlist|campaignlist|announcements|payments|messages|profile|comparatore-bonus|bonus-comparator)(\/|$)/.test(path);
  const admin = /^\/admin(\/|$)/.test(path) && path !== '/admin/login';
  const detail = /^\/affiliate-program\/[^/]+\/?$/.test(path);
  const showNavbar = internal || admin || (detail && !!user);
  const showFooter = showNavbar;
  const isThemeEnabled = showNavbar;
  const subpage = path !== '/' && !showNavbar;
  useEffect(() => {
    const language = location.pathname.match(/^\/(it|en)(?:\/|$)/)?.[1];
    if (language) i18n.changeLanguage(language);
  }, [location.pathname, i18n]);
  // Base class for all pages
  const baseClasses = "App min-h-screen";
  
  // Additional classes only for theme-enabled pages
  const themeClasses = isThemeEnabled 
    ? "bg-white dark:bg-dark-bg text-black dark:text-dark-text transition-colors duration-300" 
    : "bg-white text-black";

  return (
    <div className={`${baseClasses} ${themeClasses}`}>
      {showNavbar && <Navbar />}
      <div className={`${showNavbar ? 'lg:ml-64 pt-16 lg:pt-0' : ''} min-h-screen`}>
        <div className={`mx-auto p-0 ${showNavbar ? 'fa-interior' : subpage ? 'fa-public-page' : ''}`}>{children}</div>
      </div>
      {showFooter && <div className="lg:ml-64 fa-interior-footer"><Footer /></div>}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ConversionProvider>
          <AdminAuthProvider>
            <ThemeProvider>
              <Suspense fallback={<div role="status" className="p-10 text-center">Caricamento…</div>}>
              <Layout>
                <Analytics />
                <SeoLinks />
                <ScrollToHash />
                <Routes>
                  {/* Language wrapper handles language detection and setting */}
                  <Route path="/:lang?" element={<LanguageWrapper />}>
                    {/* Default route - renders Home component for /:lang path */}
                    <Route index element={<Home />} />
                      {/* Public routes with language prefix */}
                    <Route path="login" element={<Auth />} />
                    <Route path="signup" element={<Auth />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="faq" element={<Faq />} />
                    <Route path="unsubscribe" element={<Unsubscribe />} />                    <Route path="affiliation-program" element={<Program />} />
                    <Route path="our-partners" element={<OurPartners />} />
                    <Route path="affiliation-strategy" element={<Strategy />} />
                    <Route path="termini" element={<Termini />} />
                    <Route path="privacy" element={<Privacy />} />
                    <Route path="cookie" element={<Cookies />} />
                    <Route path="affiliate-program/:campaignId" element={<CampaignDetails />} />
                  </Route>                  {/* Admin login (without language prefix) */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  
                  {/* Unsubscribe page (without language prefix for email links) */}
                  <Route path="/unsubscribe" element={<Unsubscribe />} />

                  {/* Protected routes for regular users */}
                  <Route element={<PrivateRoute />}>
                    <Route path="/:lang/dashboard" element={<Dashboard />} />
                    <Route path="/:lang/conversionlist" element={<ConversionList />} />
                    <Route path="/:lang/campaignlist" element={<CampaignList />} />
                    <Route path="/:lang/messages" element={<UserMessages />} />
                    <Route path="/:lang/announcements" element={<UserAnnouncements />} />
                    <Route path="/:lang/profile" element={<Profile />} />
                    <Route path="/:lang/payments" element={<UserPayments />} />

                    <Route path="/:lang/comparatore-bonus" element={<BonusComparator />} />
                    <Route path="/:lang/bonus-comparator" element={<BonusComparator />} />
                    <Route path="/:lang/comparatore-bonus/:username" element={<BonusComparator />} />
                    <Route path="/:lang/bonus-comparator/:username" element={<BonusComparator />} />
                  </Route>                  {/* Protected routes for Admin */}
                  <Route element={<AdminPrivateRoute />}>
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/manage" element={<ManageCampaign />} />
                    <Route path="/admin/allconversion" element={<AllConversion />} />
                    <Route path="/admin/analytics" element={<AffiliateAnalyticsDashboard />} />
                    <Route path="/admin/messages" element={<AdminMessages />} />
                    <Route path="/admin/announcements" element={<AdminAnnouncements />} />
                    <Route path="/admin/payments" element={<AdminPayments />} />
                    <Route path="/admin/contact" element={<ContactSchema />} />
                    <Route path="/admin/newsletter" element={<NewsLetter />} />
                    <Route path="/admin/user-profile/:username" element={<UserProfile />} />
                    <Route path="/admin/business" element={<BusinessCommunications />} />
                  </Route>

                  {/* Error routes */}
                  <Route path="/403" element={<Forbidden />} />
                  <Route path="/500" element={<ServerError />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Layout>
              </Suspense>
            </ThemeProvider>
          </AdminAuthProvider>
        </ConversionProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
